import { z } from 'zod'

// Onboarding checklist state — stored on Organization.onboardingState (JSON).
// Records dismissal of the Today onboarding widget plus per-step skip and
// completion-acknowledgement events with who/when attribution. Org-level fact,
// shared across all admins/owners of the organization.

export const OnboardingStateEventSchema = z.object({
  /** ISO-8601 timestamp */
  at: z.string(),
  /** User ID of the actor */
  by: z.string(),
})

export type OnboardingStateEvent = z.infer<typeof OnboardingStateEventSchema>

export const OnboardingStateSchema = z.object({
  /** Whole-widget dismissal. Null/absent means visible. */
  dismissed: OnboardingStateEventSchema.nullable().optional(),
  /** Steps explicitly skipped by an admin. Counted as done for progress. */
  skipped: z.record(z.string(), OnboardingStateEventSchema).optional(),
  /**
   * First time the widget observed a step as complete (and by whom). An
   * attribution stamp only — it does not gate progress (real completion is
   * still derived from underlying record counts).
   */
  acknowledged: z.record(z.string(), OnboardingStateEventSchema).optional(),
})

export type OnboardingState = z.infer<typeof OnboardingStateSchema>

export function isOnboardingState(value: unknown): value is OnboardingState {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('dismissed' in value || 'skipped' in value || 'acknowledged' in value)
  )
}
